import Helpers from 'application/Shared/Helpers/Functions';

const { isset } = Helpers;

const Response = (() => {
    const has = (response) => {
        if (isset(response, 'data')) {
            return true;
        }
        return false;
    };

    const hasData = (response, opts = {}) => {
        const { noId } = opts;

        if (noId && isset(response, 'data', 'data')) {
            return true;
        }

        if (isset(response, 'data', 'data', 0, 'id')) {
            return true;
        }
        if (isset(response, 'data', 'data', 0, 'linkedId')) {
            return true;
        }
        if (isset(response, 'data', 'data', 'id')) {
            return true;
        }
        if (isset(response, 'data', 'id')) {
            return true;
        }
        return false;
    };

    const hasResponseData = (response) => {
        if (isset(response, 'data', 'data')) {
            return true;
        }
        return false;
    };

    const hasCount = (response) => {
        if (isset(response, 'data', 'data', 'count')) {
            return true;
        }
        return false;
    };

    const get = (response) => {
        if (!has(response)) {
            return {};
        }
        return response.data;
    };

    const getCount = (response) => {
        if (!hasCount(response)) {
            return 0;
        }
        return parseInt(response.data.data.count, 10);
    };

    const getId = (response) => {
        if (isset(response, 'data', 'data', 0, 'id')) {
            return response.data.data[0].id;
        }
        if (isset(response, 'data', 'data', 'id')) {
            return response.data.data.id;
        }
        if (isset(response, 'data', 'id')) {
            return response.data.id;
        }
        return false;
    };

    /**
     * Gets the data from an axios response
     * @template T
     * @param {import('axios').AxiosResponse<{data: T}>} response - The axios response object
     * @param {Object} [opts={}] - Optional parameters
     * @returns {T} The data from the response, defaults to empty array if no data
     */
    const getData = (response, opts = {}) => {
        if (!hasData(response, opts)) {
            return [];
        }

        return response.data.data;
    };

    const getTotal = (response) => {
        if (!hasData(response)) {
            return 0;
        }

        const data = getData(response);
        if (!Array.isArray(data)) {
            return 0;
        }

        return data.length;
    };

    const getResponseData = (response) => {
        if (!hasResponseData(response)) {
            return {};
        }
        return response.data.data;
    };

    const getOne = (response) => {
        if (!hasData(response)) {
            return {};
        }

        return response.data.data[0];
    };

    const getProperty = (response, key) => {
        if (!isset(response, 'data', 'data', 0, key)) {
            return false;
        }

        return response.data.data[0][key];
    };

    const getFieldMap = (response, field, options = {}) => {
        const { multiple } = options;
        let data = response;
        if (hasData(response)) {
            data = getData(response);
        }

        if (!data) {
            return {};
        }

        if (!Array.isArray(data)) {
            return {};
        }

        const map = {};
        data.forEach((el) => {
            if (!isset(el, field)) {
                return;
            }

            const key = el[field];
            if (!multiple) {
                map[key] = el;
                return;
            }

            if (!isset(map, key)) {
                map[key] = [];
            }
            map[key].push(el);
        });

        return map;
    };

    const getDistinct = (response, key = 'id', options = {}) => {
        let data = [];
        if (hasData(response)) {
            data = getData(response);
        } else if (has(response)) {
            data = response;
        }

        const ids = [];
        data.forEach((entry) => {
            if (!isset(entry, key)) {
                return;
            }

            let id = entry[key];
            if (!id) {
                return;
            }

            id = id.trim();
            if (!ids.includes(id)) {
                ids.push(id);
            }
        });

        if (options.string) {
            return `[${ids.join(',')}]`;
        }

        return ids;
    };

    const isSuccessCode = (respCode) => [200, 201].includes(respCode);

    return {
        get,
        getCount,
        getData,
        getDataCount: getTotal,
        getDistinct,
        getFieldMap,
        getId,
        getOne,
        getProperty,
        getResponseData,
        getTotal,
        has,
        hasCount,
        hasData,
        hasResponse: has,
        hasResponseData,
        isSuccessCode
    };
})();

export default Response;
